// pages/secure/ExaminerMyAccessPage.js
// What this inspection account may read, and how to ask for more.
//
// An examiner account now starts with nothing. Without this screen that is an
// unexplained wall of refusals, so the screen is part of the feature, not a
// convenience: it names every area the examination could cover, says where each
// one stands, and lets the examiner ask for what they came for — in writing,
// with a reason, which is also the brokerage's record of the scope requested.
//
// The two endpoints behind it are carved out of the access guard by name, so they
// answer even when nothing at all has been granted.
import React, { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';

const TONE = {
    granted: 'bg-green-100 text-green-800 border-green-200',
    requested: 'bg-amber-100 text-amber-800 border-amber-200',
    denied: 'bg-red-100 text-red-700 border-red-200',
    revoked: 'bg-slate-200 text-slate-600 border-slate-300',
    expired: 'bg-slate-200 text-slate-600 border-slate-300',
    not_requested: 'bg-slate-100 text-slate-500 border-slate-200',
};
const LABEL = {
    granted: 'Granted', requested: 'Awaiting decision', denied: 'Denied',
    revoked: 'Revoked', expired: 'Expired', not_requested: 'Not granted',
};

const ExaminerMyAccessPage = () => {
    const api = useApiClient();
    const [areas, setAreas] = useState([]);
    const [closed, setClosed] = useState([]);
    const [picked, setPicked] = useState({});
    const [note, setNote] = useState('');
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [sending, setSending] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await api.get('/api/examiner-access/mine');
            setAreas(res.areas || []);
            setClosed(res.closed || []);
            setPicked({});
            setError('');
        } catch (e) { setError(e.message || 'Could not load your access.'); }
    }, [api]);
    useEffect(() => { load(); }, [load]);

    // Only what is not already held can be asked for.
    const askable = areas.filter((a) => a.status !== 'granted');
    const chosen = Object.keys(picked).filter((k) => picked[k]);

    const request = async () => {
        if (!chosen.length) return;
        setSending(true); setError(''); setNotice('');
        try {
            const res = await api.post('/api/examiner-access/requests', {
                area_keys: chosen, note: note || null,
            });
            const n = (res.requested || []).length;
            setNotice(n
                ? `${n} area${n === 1 ? '' : 's'} requested. The managing broker will be notified.`
                : 'Nothing was requested.');
            setNote('');
            load();
        } catch (e) { setError(e.message || 'Could not send the request.'); } finally { setSending(false); }
    };

    return (
        <div className="p-6 max-w-4xl">
            <h1 className="text-2xl font-bold text-gray-900">My inspection access</h1>
            <p className="text-sm text-gray-500 mb-4">
                Access is granted by the managing broker, area by area — 68 Ill. Adm. Code 1450.610(c)(3)(D).
                Every area is read-only.
            </p>

            {error && <p className="mb-3 text-sm text-red-600">{error}</p>}
            {notice && <p className="mb-3 text-sm text-green-700">{notice}</p>}

            <div className="space-y-2">
                {areas.map((a) => (
                    <div key={a.area_key} className={`p-3 border rounded-md ${TONE[a.status] || TONE.not_requested}`}>
                        <div className="flex items-start gap-3">
                            {a.status !== 'granted' && (
                                <input type="checkbox" className="mt-1 h-4 w-4"
                                    checked={!!picked[a.area_key]}
                                    onChange={(e) => setPicked({ ...picked, [a.area_key]: e.target.checked })} />
                            )}
                            <div className="flex-1">
                                <div className="flex items-center gap-2">
                                    <span className="font-medium">{a.title}</span>
                                    <span className="text-xs font-semibold">{LABEL[a.status] || a.status}</span>
                                </div>
                                {a.description && <p className="text-xs mt-0.5 opacity-80">{a.description}</p>}
                                {a.statutory_ref && <p className="text-xs mt-0.5 opacity-60">{a.statutory_ref}</p>}
                                {a.expires_at && (
                                    <p className="text-xs mt-0.5 opacity-80">
                                        Access ends {new Date(a.expires_at).toLocaleDateString()}
                                    </p>
                                )}
                                {a.decision_note && (
                                    <p className="text-xs mt-1 opacity-80">Broker’s note: “{a.decision_note}”</p>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
                {!areas.length && <p className="text-sm text-slate-400">No inspection areas are defined.</p>}
            </div>

            {askable.length > 0 && (
                <div className="mt-6 bg-white border border-slate-200 rounded-lg p-4">
                    <h2 className="font-semibold text-slate-800 mb-2">Request access</h2>
                    <p className="text-sm text-slate-600 mb-3">
                        Tick the areas your examination covers and say briefly why. The request and its reason
                        are recorded with the brokerage.
                    </p>
                    <textarea rows="2" value={note} onChange={(e) => setNote(e.target.value)}
                        placeholder="e.g. Routine escrow examination, file years 2024–2026"
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm mb-3
                                   focus:outline-none focus:ring-slate-500 focus:border-slate-500" />
                    <button onClick={request} disabled={sending || !chosen.length}
                        className="px-4 py-2 text-sm bg-slate-800 text-white rounded-md hover:bg-slate-900 disabled:bg-indigo-300">
                        {sending ? 'Sending…' : `Request ${chosen.length || ''} area${chosen.length === 1 ? '' : 's'}`.trim()}
                    </button>
                </div>
            )}

            {closed.length > 0 && (
                <div className="mt-6">
                    <h2 className="text-xs font-semibold uppercase text-slate-500 mb-2">
                        Not available to inspection accounts
                    </h2>
                    <ul className="space-y-1">
                        {closed.map((a) => (
                            <li key={a.area_key} className="text-xs text-slate-500">
                                <span className="font-medium text-slate-600">{a.title}</span>
                                {a.description ? ` — ${a.description}` : ''}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default ExaminerMyAccessPage;
